import CryptoJS from 'crypto-js';
import { logger, generateRequestId } from '../utils/logger';


/**
 * Encryption Service for HIPAA-compliant data protection
 * Implements AES-256 encryption for sensitive health data at rest
 */

const ENCRYPTION_CONFIG = {
  algorithm: 'AES',
  keySize: 256,
  iterations: 10000, // PBKDF2 iterations
  saltSize: 128 / 8, // 128 bits
  ivSize: 128 / 8 // 128 bits for AES
};

export const encryptionService = {
  /**
   * Derive encryption key from user password using PBKDF2
   * @param {string} password - User's encryption password
   * @param {string} salt - Salt for key derivation (hex string)
   * @returns {string} - Derived key
   */
  deriveKey(password, salt) {
    const saltWordArray = CryptoJS?.enc?.Hex?.parse(salt);
    const key = CryptoJS?.PBKDF2(password, saltWordArray, {
      keySize: ENCRYPTION_CONFIG?.keySize / 32,
      iterations: ENCRYPTION_CONFIG?.iterations
    });
    return key?.toString();
  },

  /**
   * Generate random salt for key derivation
   * @returns {string} - Random salt (hex string)
   */
  generateSalt() {
    const salt = CryptoJS?.lib?.WordArray?.random(ENCRYPTION_CONFIG?.saltSize);
    return salt?.toString();
  },

  /**
   * Encrypt sensitive data using AES-256 (MANDATORY - automatic encryption)
   * @param {string} plaintext - Data to encrypt
   * @returns {object} - { encrypted, salt, iv, error }
   */
  encryptAutomatic(plaintext) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      if (!plaintext) {
        return { encrypted: null, salt: null, iv: null, error: 'Missing plaintext' };
      }

      // Get device-specific encryption key
      const deviceKey = this.getDeviceKey();
      
      // Generate salt for this encryption
      const encryptionSalt = this.generateSalt();
      
      // Derive key from device key
      const key = this.deriveKey(deviceKey, encryptionSalt);
      
      // Generate random IV
      const iv = CryptoJS?.lib?.WordArray?.random(ENCRYPTION_CONFIG?.ivSize);
      
      // Encrypt using AES-256
      const encrypted = CryptoJS?.AES?.encrypt(plaintext, CryptoJS?.enc?.Hex?.parse(key), {
        iv: iv,
        mode: CryptoJS?.mode?.CBC,
        padding: CryptoJS?.pad?.Pkcs7
      });

      logger?.debug('Data encrypted automatically', { requestId });

      return {
        encrypted: encrypted?.toString(),
        salt: encryptionSalt,
        iv: iv?.toString(),
        error: null
      };
    } catch (error) {
      logger?.error('Automatic encryption error', error, { requestId });
      return { encrypted: null, salt: null, iv: null, error: error?.message || 'Encryption failed' };
    }
  },

  /**
   * Decrypt encrypted data using AES-256 (automatic with device key)
   * @param {string} ciphertext - Encrypted data
   * @param {string} salt - Salt used for key derivation
   * @param {string} iv - Initialization vector used for encryption
   * @returns {object} - { decrypted, error }
   */
  decryptAutomatic(ciphertext, salt, iv) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      if (!ciphertext || !salt || !iv) {
        return { decrypted: null, error: 'Missing required decryption parameters' };
      }

      // Get device-specific encryption key
      const deviceKey = this.getDeviceKey();
      
      // Derive key from device key
      const key = this.deriveKey(deviceKey, salt);
      
      // Parse IV
      const ivWordArray = CryptoJS?.enc?.Hex?.parse(iv);
      
      // Decrypt using AES-256
      const decrypted = CryptoJS?.AES?.decrypt(ciphertext, CryptoJS?.enc?.Hex?.parse(key), {
        iv: ivWordArray,
        mode: CryptoJS?.mode?.CBC,
        padding: CryptoJS?.pad?.Pkcs7
      });

      const decryptedText = decrypted?.toString(CryptoJS?.enc?.Utf8);

      if (!decryptedText) {
        return { decrypted: null, error: 'Decryption failed - corrupted data' };
      }

      logger?.debug('Data decrypted automatically', { requestId });

      return {
        decrypted: decryptedText,
        error: null
      };
    } catch (error) {
      logger?.error('Automatic decryption error', error, { requestId });
      return { decrypted: null, error: 'Decryption failed - corrupted data' };
    }
  },

  /**
   * Encrypt sensitive fields in symptom entry
   * @param {object} entry - Symptom entry data
   * @param {string} password - Encryption password
   * @returns {object} - Entry with encrypted fields
   */
  encryptSymptomEntry(entry, password) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const sensitiveFields = ['notes', 'medication_details', 'voice_note'];
      const encryptedEntry = { ...entry };
      const encryptionMetadata = {};

      // Generate single salt for all fields in this entry
      const salt = this.generateSalt();

      sensitiveFields?.forEach(field => {
        if (entry?.[field]) {
          const { encrypted, iv, error } = this.encrypt(entry?.[field], password, salt);
          if (!error) {
            encryptedEntry[field] = encrypted;
            encryptionMetadata[`${field}_iv`] = iv;
          } else {
            logger?.warn(`Failed to encrypt field: ${field}`, { requestId, error });
          }
        }
      });

      // Store encryption metadata
      encryptedEntry.encryption_metadata = {
        salt,
        encrypted_fields: sensitiveFields?.filter(f => entry?.[f]),
        ...encryptionMetadata,
        encrypted_at: new Date()?.toISOString()
      };

      logger?.audit('SYMPTOM_ENTRY_ENCRYPTED', null, { requestId, fields: Object.keys(encryptionMetadata) });

      return { data: encryptedEntry, error: null };
    } catch (error) {
      logger?.error('Symptom entry encryption error', error, { requestId });
      return { data: null, error: error?.message || 'Failed to encrypt entry' };
    }
  },

  /**
   * Decrypt sensitive fields in symptom entry
   * @param {object} entry - Encrypted symptom entry
   * @param {string} password - Decryption password
   * @returns {object} - Entry with decrypted fields
   */
  decryptSymptomEntry(entry, password) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      if (!entry?.encryption_metadata) {
        // Entry is not encrypted
        return { data: entry, error: null };
      }

      const decryptedEntry = { ...entry };
      const { salt, encrypted_fields } = entry?.encryption_metadata;

      encrypted_fields?.forEach(field => {
        if (entry?.[field]) {
          const iv = entry?.encryption_metadata?.[`${field}_iv`];
          const { decrypted, error } = this.decrypt(entry?.[field], password, salt, iv);
          if (!error) {
            decryptedEntry[field] = decrypted;
          } else {
            logger?.warn(`Failed to decrypt field: ${field}`, { requestId, error });
            decryptedEntry[field] = '[Encrypted - Unable to decrypt]';
          }
        }
      });

      return { data: decryptedEntry, error: null };
    } catch (error) {
      logger?.error('Symptom entry decryption error', error, { requestId });
      return { data: null, error: error?.message || 'Failed to decrypt entry' };
    }
  },

  /**
   * Create encrypted export archive
   * @param {object} exportData - Data to export
   * @param {string} password - Archive password
   * @param {string} filename - Base filename
   * @returns {object} - { encrypted, metadata }
   */
  async createEncryptedArchive(exportData, password, filename = 'health-data-export') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      // Convert export data to JSON string
      const jsonData = JSON.stringify(exportData, null, 2);
      
      // Encrypt the JSON data
      const { encrypted, salt, iv, error } = this.encrypt(jsonData, password);
      
      if (error) {
        return { data: null, error };
      }

      // Create metadata file
      const metadata = {
        filename: `${filename}.encrypted`,
        created_at: new Date()?.toISOString(),
        encryption: {
          algorithm: 'AES-256-CBC',
          key_derivation: 'PBKDF2',
          iterations: ENCRYPTION_CONFIG?.iterations,
          salt,
          iv
        },
        data_summary: {
          total_entries: exportData?.symptoms?.length || 0,
          date_range: exportData?.dateRange || null
        }
      };

      logger?.audit('ENCRYPTED_ARCHIVE_CREATED', null, { requestId, filename });

      return {
        data: {
          encrypted,
          metadata
        },
        error: null
      };
    } catch (error) {
      logger?.error('Archive encryption error', error, { requestId });
      return { data: null, error: error?.message || 'Failed to create encrypted archive' };
    }
  },

  /**
   * Decrypt export archive
   * @param {string} encrypted - Encrypted archive data
   * @param {object} metadata - Archive metadata
   * @param {string} password - Archive password
   * @returns {object} - Decrypted export data
   */
  async decryptArchive(encrypted, metadata, password) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { salt, iv } = metadata?.encryption;
      
      const { decrypted, error } = this.decrypt(encrypted, password, salt, iv);
      
      if (error) {
        return { data: null, error };
      }

      // Parse JSON data
      const exportData = JSON.parse(decrypted);

      logger?.audit('ENCRYPTED_ARCHIVE_DECRYPTED', null, { requestId });

      return { data: exportData, error: null };
    } catch (error) {
      logger?.error('Archive decryption error', error, { requestId });
      return { data: null, error: 'Failed to decrypt archive - invalid password or corrupted data' };
    }
  },

  /**
   * Validate encryption password strength
   * @param {string} password - Password to validate
   * @returns {object} - { valid, strength, message }
   */
  validatePassword(password) {
    if (!password || password?.length < 12) {
      return { valid: false, strength: 'weak', message: 'Password must be at least 12 characters' };
    }

    const hasUpperCase = /[A-Z]/?.test(password);
    const hasLowerCase = /[a-z]/?.test(password);
    const hasNumbers = /[0-9]/?.test(password);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/?.test(password);

    const criteriaCount = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChars]?.filter(Boolean)?.length;

    if (criteriaCount < 3) {
      return { 
        valid: false, 
        strength: 'weak', 
        message: 'Password must contain uppercase, lowercase, numbers, and special characters' 
      };
    }

    if (password?.length >= 16 && criteriaCount === 4) {
      return { valid: true, strength: 'strong', message: 'Strong password' };
    }

    return { valid: true, strength: 'medium', message: 'Medium strength password' };
  }
};

export default encryptionService;